import React, { useState, useEffect, useRef } from 'react';
import {
  getParticipatingTenders,
  updateParticipatingTender,
  getParticipatingTenderOptions,
  addParticipatingTask,
  updateParticipatingTask,
  deleteParticipatingTask,
  archiveWonTender,
  deleteLostTender,
  getAssignableUsers,
} from '../services/api';
import TaskChecklist from './TaskChecklist';
import StagePill from './StagePill';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatDate';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { useFocusTrap } from '../hooks/useFocusTrap';
import TechnicalBidDetail from './TechnicalBidDetail';
import AssignTechnicalBidModal from './AssignTechnicalBidModal';
import AssignFinancialBidModal from './AssignFinancialBidModal';
import FinancialBidDetail from './FinancialBidDetail';
import TenderViewModal from './TenderViewModal';
import './TenderList.css';

function ParticipatingTenderList({ user, canManage = false, fixedStatus = '', onGoToDiscovery }) {
  const { toast } = useToast();
  const statusModalRef = useRef(null);
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(fixedStatus || '');
  const [options, setOptions] = useState({ sources: [], statuses: [] });

  const [viewingTender, setViewingTender] = useState(null);
  const [statusUpdateTender, setStatusUpdateTender] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [wonValueInput, setWonValueInput] = useState('');
  const [lostReasonInput, setLostReasonInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [assignTBTender, setAssignTBTender] = useState(null);
  const [technicalBidDetailId, setTechnicalBidDetailId] = useState(null);
  const [assignFBTender, setAssignFBTender] = useState(null);
  const [financialBidDetailId, setFinancialBidDetailId] = useState(null);
  const [showStagger, setShowStagger] = useState(false);
  const staggerShownRef = useRef(false);
  const [assignableUsers, setAssignableUsers] = useState([]);

  const closeStatusModal = () => {
    setStatusUpdateTender(null);
    setNewStatus('');
    setWonValueInput('');
    setLostReasonInput('');
  };

  const isStatusModalOpen = !!statusUpdateTender;
  useModalScrollLock(isStatusModalOpen);
  useFocusTrap(statusModalRef, isStatusModalOpen, closeStatusModal);

  // Source options (static)
  const sourceOptions = [
    { value: 'gem', label: 'GeM (Government e-Marketplace)' },
    { value: 'cppp', label: 'CPPP (Central Public Procurement Portal)' },
    { value: 'state', label: 'State Portals' },
    { value: 'private', label: 'Private' },
  ];

  // Fetch options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await getParticipatingTenderOptions();
        if (response.success) {
          setOptions(response.options);
        }
      } catch (err) {
        console.error('Failed to fetch options:', err);
      }
    };
    const fetchUsers = async () => {
      try {
        const res = await getAssignableUsers();
        if (res.success) setAssignableUsers(res.users || []);
      } catch { /* ignore */ }
    };
    fetchOptions();
    fetchUsers();
  }, []);

  // Fetch tenders (optional overrides for e.g. clear filters)
  const fetchTenders = async (overrides = {}) => {
    try {
      setLoading(true);
      const effectiveStatus = fixedStatus || (overrides.status !== undefined ? overrides.status : statusFilter);
      const response = await getParticipatingTenders({
        source: overrides.source !== undefined ? overrides.source : sourceFilter,
        status: effectiveStatus || (overrides.status !== undefined ? overrides.status : statusFilter),
        search: overrides.search !== undefined ? overrides.search : searchTerm,
      });
      if (response.success) {
        setTenders(response.tenders);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter, statusFilter, fixedStatus]);

  useEffect(() => {
    if (!loading && tenders.length > 0 && !staggerShownRef.current) {
      staggerShownRef.current = true;
      setShowStagger(true);
    }
  }, [loading, tenders.length]);

  useEffect(() => {
    if (!showStagger) return;
    const t = setTimeout(() => setShowStagger(false), 800);
    return () => clearTimeout(t);
  }, [showStagger]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchTenders();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSourceFilter('');
    if (!fixedStatus) setStatusFilter('');
  };

  const hasActiveFilters = searchTerm || sourceFilter || (statusFilter && !fixedStatus);

  const handleStatusUpdate = async () => {
    if (!newStatus || !statusUpdateTender) return;
    try {
      setActionLoading(true);
      const payload = { status: newStatus };
      if (newStatus === 'won' && wonValueInput) {
        payload.won_value = parseFloat(wonValueInput);
      }
      if (newStatus === 'lost' && lostReasonInput.trim()) {
        payload.lost_reason = lostReasonInput.trim();
      }
      const response = await updateParticipatingTender(statusUpdateTender.id, payload);
      if (response.success) {
        setStatusUpdateTender(null);
        setNewStatus('');
        setWonValueInput('');
        setLostReasonInput('');
        fetchTenders();
        if (viewingTender?.id === statusUpdateTender.id) setViewingTender(response.tender);
        toast.success('Tender status updated');
      }
    } catch (err) {
      const message = err?.data?.error || err?.message || 'Failed to update status';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async (tender) => {
    if (!window.confirm(`Archive won tender "${tender.tender_ref}"? It will be moved to the archive collection.`)) return;
    try {
      setActionLoading(true);
      const res = await archiveWonTender(tender.id);
      if (res.success) {
        toast.success('Tender archived successfully');
        fetchTenders();
      }
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Failed to archive tender');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLost = async (tender) => {
    if (!window.confirm(`Permanently delete lost tender "${tender.tender_ref}"? This cannot be undone.`)) return;
    try {
      setActionLoading(true);
      const res = await deleteLostTender(tender.id);
      if (res.success) {
        toast.success('Lost tender deleted');
        fetchTenders();
      }
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Failed to delete tender');
    } finally {
      setActionLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get source badge class
  const getSourceClass = (source) => {
    const sourceClasses = {
      gem: 'source-gem',
      cppp: 'source-cppp',
      state: 'source-state',
      private: 'source-private',
    };
    return sourceClasses[source] || 'source-default';
  };

  // Check if deadline is approaching (within 7 days)
  const isDeadlineApproaching = (dateStr) => {
    if (!dateStr) return false;
    const deadline = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  // Check if deadline has passed
  const isDeadlinePassed = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  // eslint-disable-next-line no-unused-vars
  const isEmdExpired = (dateStr) => dateStr && new Date(dateStr) < new Date();
  // eslint-disable-next-line no-unused-vars
  const isEmdExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 7;
  };

  const emdStatusClass = (status) => {
    const map = { created: 'status-participating', submitted: 'status-submitted', locked: 'status-won', released: 'status-default', forfeited: 'status-lost', closed: 'status-default' };
    return map[status] || 'status-default';
  };

  const emdStatusDisplay = (status) => {
    if (!status) return '–';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const technicalBidStatusClass = (status) => {
    const map = {
      assigned: 'status-participating',
      in_progress: 'status-participating',
      submitted: 'status-submitted',
      approved: 'status-won',
      rejected: 'status-lost',
    };
    return map[status] || 'status-default';
  };

  const canAssignTechnicalBid = (tender) =>
    canManage &&
    tender.status === 'participating' &&
    !tender.technical_bid_id;

  const canAssignFinancialBid = (tender) =>
    canManage &&
    tender.status === 'participating' &&
    !tender.financial_bid_id;

  const financialBidStatusClass = (status) => {
    const map = {
      assigned: 'status-participating',
      in_progress: 'status-participating',
      submitted: 'status-submitted',
      approved: 'status-won',
      rejected: 'status-lost',
    };
    return map[status] || 'status-default';
  };

  return (
    <div className="tender-list-container">
      <div className="tender-list-header">
        <div className="header-title">
          <h2>
            {fixedStatus === 'won' && 'Won Tenders'}
            {fixedStatus === 'lost' && 'Lost Tenders'}
            {fixedStatus === 'dropped' && 'Dropped Tenders'}
            {fixedStatus === 'canceled' && 'Cancelled Tenders'}
            {!fixedStatus && 'Participating Tenders'}
          </h2>
          <p>
            {fixedStatus === 'won' && 'Tenders that were won'}
            {fixedStatus === 'lost' && 'Tenders that were lost'}
            {fixedStatus === 'dropped' && 'Tenders that were dropped'}
            {fixedStatus === 'canceled' && 'Tenders that were cancelled'}
            {!fixedStatus && 'Tenders that have been approved for participation'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search by reference, title, or buyer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit">Search</button>
        </form>
        
        <div className="filter-dropdowns">
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="">All Sources</option>
            {sourceOptions.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          
          {!fixedStatus && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              {options.statuses?.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          )}
          {hasActiveFilters && (
            <button type="button" className="btn-secondary" onClick={() => { clearFilters(); fetchTenders({ search: '', source: '', status: '' }); }} style={{ marginLeft: 8 }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State - skeleton table */}
      {loading ? (
        <div className="tenders-table-wrapper">
          <table className="tenders-table table-skeleton">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>Reference</th>
                <th>Title</th>
                <th>Source</th>
                <th>Buyer / Department</th>
                <th>Deadline</th>
                <th>Est. Value</th>
                <th>Products</th>
                <th>Status</th>
                <th>Pre-bid</th>
                <th>EMD Status</th>
                <th>Technical Bid</th>
                <th>Price Bid</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  <td><span className="skeleton-box" style={{ width: 20 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: '60%' }} /></td>
                  <td><span className="skeleton-box" style={{ width: 72 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 100 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 88 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 70 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 52 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 80 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 36 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 70 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 80 }} /></td>
                  <td><span className="skeleton-box" style={{ width: 80 }} /></td>
                  {canManage && <td><span className="skeleton-box" style={{ width: 60 }} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tenders.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
          <h3>No participating tenders</h3>
          <p>Tenders approved for participation will appear here</p>
          {onGoToDiscovery && (
            <button type="button" onClick={onGoToDiscovery}>Go to Tender Discovery</button>
          )}
        </div>
      ) : (
        /* Tenders Table */
        <div className="tenders-table-wrapper">
          <table className="tenders-table">
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                <th>Reference</th>
                <th>Title</th>
                <th>Source</th>
                <th>Buyer / Department</th>
                <th>Deadline</th>
                <th>Est. Value</th>
                <th>Products</th>
                <th>Status</th>
                <th title="Pre-bid checklist">Pre-bid</th>
                <th>EMD Status</th>
                <th>Technical Bid</th>
                <th>Price Bid</th>
                {canManage && <th>Actions</th>}
              </tr>
            </thead>
            <tbody className={showStagger ? 'stagger-rows' : ''}>
              {tenders.map((tender, index) => (
                <React.Fragment key={tender.id}>
                  <tr
                    style={showStagger ? { animationDelay: `${index * 0.03}s` } : undefined}
                    onClick={() => setExpandedId(expandedId === tender.id ? null : tender.id)}
                    className={`tender-row tender-row-clickable ${isDeadlinePassed(tender.submission_deadline) ? 'deadline-passed' : ''} ${expandedId === tender.id ? 'expanded' : ''}`}
                  >
                    <td className="expand-cell">
                      <svg className={`expand-icon ${expandedId === tender.id ? 'expanded' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ref-code ref-code-link"
                        onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}
                        title="View tender details"
                      >
                        {tender.tender_ref}
                      </button>
                    </td>
                    <td>
                      <span className="tender-title-inline">{tender.title || '–'}</span>
                    </td>
                    <td>
                      <span className={`source-badge ${getSourceClass(tender.source)}`}>
                        {tender.source_display}
                      </span>
                    </td>
                    <td>{tender.buyer_name || '-'}</td>
                    <td>
                      <span className={`deadline ${isDeadlineApproaching(tender.submission_deadline) ? 'approaching' : ''} ${isDeadlinePassed(tender.submission_deadline) ? 'passed' : ''}`}>
                        {formatDate(tender.submission_deadline)}
                      </span>
                    </td>
                    <td>{formatCurrency(tender.tender_value)}</td>
                    <td>
                      <span className="product-count">{(tender.products && tender.products.length) || 0} items</span>
                    </td>
                    <td>
                      <StagePill status={tender.status} />
                    </td>
                    <td title={tender.pre_bid_complete ? 'All gates complete' : 'Pre-bid checklist'}>
                      {tender.pre_bid_percent != null ? (
                        <span className={`pre-bid-badge ${tender.pre_bid_complete ? 'pre-bid-complete' : ''}`}>
                          {tender.pre_bid_completed_count}/{tender.pre_bid_total_count}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>–</span>
                      )}
                    </td>
                    <td>
                      {tender.emd_status ? (
                        <span className={`status-badge ${emdStatusClass(tender.emd_status)}`} title="EMD status">
                          {emdStatusDisplay(tender.emd_status)}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>–</span>
                      )}
                    </td>
                    <td>
                      {tender.technical_bid_id ? (
                        <button
                          type="button"
                          className="link-btn"
                          style={{ padding: 0, fontSize: '13px' }}
                          onClick={(e) => { e.stopPropagation(); setTechnicalBidDetailId(tender.technical_bid_id); }}
                        >
                          <span className={`status-badge ${technicalBidStatusClass(tender.technical_bid_status)}`} style={{ marginRight: '6px' }}>
                            {tender.technical_bid_status_display || tender.technical_bid_status}
                          </span>
                          View
                        </button>
                      ) : canAssignTechnicalBid(tender) ? (
                        <button
                          type="button"
                          className="link-btn"
                          style={{ padding: 0, fontSize: '13px' }}
                          onClick={(e) => { e.stopPropagation(); setAssignTBTender(tender); }}
                        >
                          Assign
                        </button>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>–</span>
                      )}
                    </td>
                    <td>
                      {tender.financial_bid_id ? (
                        <button
                          type="button"
                          className="link-btn"
                          style={{ padding: 0, fontSize: '13px' }}
                          onClick={(e) => { e.stopPropagation(); setFinancialBidDetailId(tender.financial_bid_id); }}
                        >
                          <span className={`status-badge ${financialBidStatusClass(tender.financial_bid_status)}`} style={{ marginRight: '6px' }}>
                            {tender.financial_bid_status_display || tender.financial_bid_status}
                          </span>
                          View
                        </button>
                      ) : canAssignFinancialBid(tender) ? (
                        <button
                          type="button"
                          className="link-btn"
                          style={{ padding: 0, fontSize: '13px' }}
                          onClick={(e) => { e.stopPropagation(); setAssignFBTender(tender); }}
                        >
                          Assign
                        </button>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>–</span>
                      )}
                    </td>
                    {canManage && (
                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="edit-btn"
                          onClick={() => {
                            setStatusUpdateTender(tender);
                            setNewStatus(tender.status);
                          }}
                          title="Update Status"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="edit-btn"
                          onClick={() => setViewingTender(tender)}
                          title="View full details"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        {tender.status === 'won' && (
                          <button
                            type="button"
                            className="edit-btn archive-btn"
                            onClick={() => handleArchive(tender)}
                            disabled={actionLoading}
                            title="Archive this won tender"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="21 8 21 21 3 21 3 8"></polyline>
                              <rect x="1" y="3" width="22" height="5"></rect>
                              <line x1="10" y1="12" x2="14" y2="12"></line>
                            </svg>
                          </button>
                        )}
                        {tender.status === 'lost' && (
                          <button
                            type="button"
                            className="edit-btn delete-btn"
                            onClick={() => handleDeleteLost(tender)}
                            disabled={actionLoading}
                            title="Delete this lost tender"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {expandedId === tender.id && (
                    <tr className="tender-detail-row">
                      <td colSpan={canManage ? 12 : 11}>
                        <div className="tender-expanded-details">
                          <div className="tender-details-grid">
                            <div className="detail-item">
                              <label>Buyer / Department</label>
                              <span>{tender.buyer_name || '-'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Submission Deadline</label>
                              <span>{formatDate(tender.submission_deadline)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Estimated Value</label>
                              <span>{formatCurrency(tender.tender_value)}</span>
                            </div>
                            <div className="detail-item">
                              <label>EMD Required</label>
                              <span>{formatCurrency(tender.emd_amount)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Products</label>
                              <span>{(tender.products && tender.products.length) || 0} items</span>
                            </div>
                            {tender.emd_status && (
                              <div className="detail-item">
                                <label>EMD Status</label>
                                <span className={`status-badge ${emdStatusClass(tender.emd_status)}`}>
                                  {tender.emd_status.charAt(0).toUpperCase() + tender.emd_status.slice(1)}
                                </span>
                              </div>
                            )}
                          </div>
                          {tender.description && (
                            <p className="tender-description-snippet">{tender.description}</p>
                          )}
                          <TaskChecklist
                            tasks={tender.tasks || []}
                            canManage={canManage}
                            users={assignableUsers}
                            onAdd={async (taskData) => {
                              try {
                                await addParticipatingTask(tender.id, taskData);
                                fetchTenders();
                              } catch (err) { alert(err.message); }
                            }}
                            onUpdate={async (taskId, data) => {
                              try {
                                await updateParticipatingTask(tender.id, taskId, data);
                                fetchTenders();
                              } catch (err) { alert(err.message); }
                            }}
                            onDelete={async (taskId) => {
                              try {
                                await deleteParticipatingTask(tender.id, taskId);
                                fetchTenders();
                              } catch (err) { alert(err.message); }
                            }}
                          />
                          <button type="button" className="link-btn" onClick={(e) => { e.stopPropagation(); setViewingTender(tender); }}>
                            View full details →
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Total Count */}
      {!loading && tenders.length > 0 && (
        <div className="list-footer">
          <span>Total: {tenders.length} tenders</span>
        </div>
      )}

      {/* Tender View Modal */}
      {viewingTender && (
        <TenderViewModal
          tender={viewingTender}
          canManage={canManage}
          onClose={() => setViewingTender(null)}
          onOpenTechnicalBid={(id) => {
            setViewingTender(null);
            setTechnicalBidDetailId(id);
          }}
          onOpenFinancialBid={(id) => {
            setViewingTender(null);
            setFinancialBidDetailId(id);
          }}
          onUpdateStatus={(t) => {
            setViewingTender(null);
            setStatusUpdateTender(t);
            setNewStatus(t.status);
          }}
        />
      )}

      {/* Assign Technical Bid Modal */}
      {assignTBTender && (
        <AssignTechnicalBidModal
          tender={assignTBTender}
          onClose={() => setAssignTBTender(null)}
          onSuccess={(tb) => {
            fetchTenders();
            if (tb?.id) setTechnicalBidDetailId(tb.id);
          }}
        />
      )}

      {/* Technical Bid Detail Modal */}
      {technicalBidDetailId && (
        <TechnicalBidDetail
          technicalBidId={technicalBidDetailId}
          user={user}
          canManage={canManage}
          onClose={() => { setTechnicalBidDetailId(null); fetchTenders(); }}
        />
      )}

      {/* Assign Price Bid Modal */}
      {assignFBTender && (
        <AssignFinancialBidModal
          tender={assignFBTender}
          onClose={() => setAssignFBTender(null)}
          onSuccess={(fb) => {
            fetchTenders();
            if (fb?.id) setFinancialBidDetailId(fb.id);
          }}
        />
      )}

      {/* Price Bid Detail Modal */}
      {financialBidDetailId && (
        <FinancialBidDetail
          financialBidId={financialBidDetailId}
          user={user}
          canManage={canManage}
          onClose={() => { setFinancialBidDetailId(null); fetchTenders(); }}
        />
      )}

      {/* Status Update Modal */}
      {statusUpdateTender && (
        <div className="modal-overlay" onClick={closeStatusModal} role="presentation">
          <div
            ref={statusModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="status-update-modal-title"
            className="confirm-modal modal--sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="status-update-modal-title">Update Tender Status</h3>
            <p>Update status for <strong>{statusUpdateTender.tender_ref}</strong></p>
            <div style={{ margin: '20px 0' }}>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px'
                }}
              >
                {options.statuses?.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {newStatus === 'submitted' && statusUpdateTender.pre_bid_complete === false && (
                <p className="pre-bid-warning" style={{ marginTop: '12px', fontSize: '13px', color: 'var(--color-warning, #b45309)' }}>
                  Pre-bid checklist is incomplete. Complete EMD, Technical bid approval, and Finance sign-off before submitting.
                </p>
              )}
              {newStatus === 'won' && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>
                    Won Value (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={statusUpdateTender.tender_value ? `Est. ₹${Number(statusUpdateTender.tender_value).toLocaleString('en-IN')}` : 'Actual contract value'}
                    value={wonValueInput}
                    onChange={(e) => setWonValueInput(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                    }}
                  />
                  <p style={{ fontSize: '12px', color: 'var(--color-text-muted, #6b7280)', marginTop: '4px' }}>
                    Leave blank to use the estimated tender value.
                  </p>
                </div>
              )}
              {newStatus === 'lost' && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}>
                    Reason for loss
                  </label>
                  <textarea
                    placeholder="Why was this tender lost?"
                    value={lostReasonInput}
                    onChange={(e) => setLostReasonInput(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      resize: 'vertical',
                    }}
                  />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={closeStatusModal}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-go-ahead-btn" 
                onClick={handleStatusUpdate}
                disabled={actionLoading}
              >
                {actionLoading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParticipatingTenderList;
